<template>
  <div class="season">
    <app-loader v-if="loading" />
    <div
      v-else
      class="season__details"
    >
      <section
        :style="{backgroundImage: getImgSrc}"
        class="season__top"
      >
        <v-container class="pt-2 pb-0 px-2 mb-0">
          <v-layout row>
            <v-breadcrumbs>
              <template v-slot="divider">
                <v-icon dark>
                  arrow_right
                </v-icon>
              </template>
              <v-breadcrumbs-item
                to="/"
                exact
                class="breadcrumbs"
              >
                Startseite
              </v-breadcrumbs-item>
              <v-breadcrumbs-item
                to="/serien"
                exact
                class="breadcrumbs"
              >
                Serien
              </v-breadcrumbs-item>
              <v-breadcrumbs-item
                :to="`/serie/${tvId}`"
                exact
                class="breadcrumbs"
              >
                {{ tvName }}
              </v-breadcrumbs-item>
              <v-breadcrumbs-item disabled>
                {{ seasonName }}
              </v-breadcrumbs-item>
            </v-breadcrumbs>
            <v-spacer />
            <v-btn
              small
              flat
              class="movie__back-btn white--text caption"
              @click="$router.go(-1)"
            >
              <v-icon
                left
                dark
                class="ma-0"
              >
                arrow_left
              </v-icon>Zurück
            </v-btn>
          </v-layout>
        </v-container>
        <v-container class="py-0">
          <div class="season__top-wrapper">
            <div
              v-if="!isMobile"
              class="season__picture"
            >
              <v-img
                class="season__img elevation-7"
                :src="posterPath ? `${medImgPrefix}${posterPath}` : noPosterSrc "
                :lazy-src="gradient"
                :fade-transition="true"
              >
                <v-layout
                  slot="placeholder"
                  fill-height
                  align-center
                  justify-center
                  ma-0
                >
                  <v-progress-circular
                    indeterminate
                    :width="3"
                    color="red"
                  />
                </v-layout>
              </v-img>
            </div>
            <div class="season__top-content">
              <div class="season__name">
                {{ tvName }} - {{ seasonName }} ({{ airDate | getYear }})
              </div>
              <div class="season__genres">
                <v-btn
                  v-for="(genre, gId) in genres"
                  :key="gId"
                  small
                  flat
                  class="white--text"
                  @click="onGenreClick(genre)"
                >
                  {{ genre.name }}
                </v-btn>
              </div>
              <div class="season__actions">
                <v-tooltip
                  v-if="voteAverage && voteAverage > 0"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-progress-circular
                      :rotate="-90"
                      :size="40"
                      :value="voteAverage * 10"
                      :color="getVoteColor(voteAverage)"
                      class="mr-2"
                      v-on="on"
                    >
                      <span class="caption">{{ Math.round(voteAverage * 10) }}%</span>
                    </v-progress-circular>
                  </template>
                  <span>Gefiel durchschnittlich {{ Math.round(voteAverage * 10) }}% der Nutzer</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <app-trailer-modal
                    slot="activator"
                    :video-id="trailer ? trailer.key : ''"
                  />
                  <span v-if="trailer">{{ trailer.name }}</span>
                  <span v-else>Do gibts no nix</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <app-carousel
                    slot="activator"
                    :images="posters"
                  />
                  <span v-if="posters.length !== 0">Weitere Bilder aus der Staffel</span>
                  <span v-else>Gibd no koa buidl</span>
                </v-tooltip>
              </div>
              <div class="season__items">
                <p class="my-1">
                  Erstausstrahlung: {{ parseLocalDate(airDate) }} - {{ episodes.length }} Folgen
                </p>
                <h3
                  v-if="overview"
                  class="season__items-title"
                >
                  Handlung:
                </h3>
                <p class="season__items-overview">
                  {{ overview }}
                </p>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <section class="season__bottom">
        <v-container>
          <div class="season__episodes mb-2">
            <h3 class="season__episodes-title">
              Folgen:
            </h3>
            <v-container
              fluid
              pa-2
            >
              <v-layout
                row
                wrap
              >
                <v-flex
                  v-for="episode in episodes"
                  :key="episode.id"
                  xs12
                  lg6
                  pr-2
                  pb-3
                >
                  <v-card
                    class="white--text season__episode-card"
                    hover
                  >
                    <v-layout style="min-height: 160px;">
                      <v-flex
                        v-bind="$vuetify.breakpoint.lgAndUp ? {['xs5']: true} : {['xs3']: true}"
                      >
                        <v-img
                          :src="episode.still_path ? `${medImgPrefix}${episode.still_path}` : noBannerSrc"
                          contain
                        />
                      </v-flex>
                      <v-flex
                        v-bind="$vuetify.breakpoint.lgAndUp ? {['xs7']: true} : {['xs9']: true}"
                      >
                        <v-card-title class="pt-2 px-3 pb-0">
                          <h3 class="season__episode-title">
                            Folge {{ episode.episode_number }} - {{ episode.name }}
                          </h3>

                          <div class="season__episode-content">
                            <p class="caption ma-0">
                              <!-- {{ $vuetify.breakpoint.lgAndUp ? (episode.overview | readMore(50, '...')) : (episode.overview | readMore(250, '...')) }} -->
                              {{ getReadMoreText(episode.overview) }}
                            </p>
                            <app-details-modal
                              slot="activator"
                              :items="episode"
                            />
                          </div>
                        </v-card-title>
                      </v-flex>
                    </v-layout>
                    <v-divider />
                    <v-card-actions>
                      <v-tooltip
                        v-if="episode.vote_average && episode.vote_average > 0"
                        top
                      >
                        <template v-slot:activator="{ on }">
                          <v-progress-circular
                            :rotate="-90"
                            :size="37"
                            :value="episode.vote_average * 10"
                            :color="getVoteColor(episode.vote_average)"
                            class="mr-3"
                            v-on="on"
                          >
                            <span class="caption">{{ Math.round(episode.vote_average * 10) }}%</span>
                          </v-progress-circular>
                        </template>
                        <span>Gefiel durchschnittlich {{ Math.round(episode.vote_average * 10) }}% der Nutzer</span>
                      </v-tooltip>
                      <span class="caption">Veröffentlicht: {{ parseLocalDate(episode.air_date) }}</span>
                      <v-spacer />
                      <v-tooltip top>
                        <v-btn
                          slot="activator"
                          icon
                          @click="showMore"
                        >
                          <v-icon>favorite</v-icon>
                        </v-btn>
                        <span>Favoriten</span>
                      </v-tooltip>
                      <v-tooltip top>
                        <v-btn
                          slot="activator"
                          icon
                          @click="showMore"
                        >
                          <v-icon>bookmark</v-icon>
                        </v-btn>
                        <span>Merken</span>
                      </v-tooltip>
                      <!-- <v-tooltip top>
                        <v-btn
                          slot="activator"
                          icon
                          @click="showMore"
                        >
                          <v-icon>{{ showEpisodeContent ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
                        </v-btn>
                        <span>Details anzeigen</span>
                      </v-tooltip> -->
                    </v-card-actions>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-container>
          </div>

          <app-person-overview
            v-if="cast.length > 0"
            :credits="cast"
          />
        </v-container>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';

import { base } from '../../config/config';
import TrailerModal from '../../components/TrailerModal.vue';
import Carousel from '../../components/PersonCarousel.vue';
import PersonOverview from '../../components/PersonOverview.vue';
import EpisodeDetailsModal from '../../components/tvShows/EpisodeDetailModal.vue';

const noBannerSrc = require('../../assets/no-banner.jpg');
const noPosterSrc = require('../../assets/noposter.jpg');
const noAvatarSrc = require('../../assets/avatar.jpg');
const gradient = require('../../assets/gradient.jpg');

export default {
  name: 'SeasonDetail',
  components: {
    appTrailerModal: TrailerModal,
    appCarousel: Carousel,
    appPersonOverview: PersonOverview,
    appDetailsModal: EpisodeDetailsModal,
  },
  filters: {
    getYear(val) {
      if (val) {
        return val.split('-').slice(0, 1).join('');
      }
      return '';
    },
  },
  beforeRouteUpdate(to, from, next) {
    this.tvId = parseInt(to.params.id, 10);
    this.seasonNumber = parseInt(to.params.season_id, 10);
    this.initSeasonPage();
    this.checkScreenWidth();
    next();
  },
  data() {
    return {
      noBannerSrc,
      noPosterSrc,
      noAvatarSrc,
      gradient,
      imgPrefix: base.imgPrefix,
      lowImgPrefix: base.lowImgPrefix,
      medImgPrefix: base.medImgPrefix,
      posterPrefix: base.posterPrefix,
      profilePrefix: base.medProfilePrefix,
      isMobile: false,
      tvId: parseInt(this.$route.params.id, 10),
      tvName: '',
      backdropPath: '',
      genres: [],
      seasonNumber: parseInt(this.$route.params.season_number, 10),
      posterPath: '',
      seasonName: '',
      airDate: '',
      overview: '',
      episodes: [],
      episodesCast: [],
      episodesGuests: [],
      cast: [],
      crew: [],
      posters: [],
      videos: [],
      trailer: {},
      voteAverage: 0.0,
      showEpisodeContent: false,
      activeEpisode: null,
    };
  },
  computed: {
    loading() {
      return this.$store.getters.loading;
    },
    getImgSrc() {
      return this.backdropPath
        ? `linear-gradient(to bottom, rgba(77, 77, 77, 0.5), rgba(77, 77, 77, 0.3)), url("${
          this.posterPrefix
        }${this.backdropPath}")`
        : `linear-gradient(to bottom, rgba(77, 77, 77, 0.5), rgba(77, 77, 77, 0.3)), url("${
          this.noBannerSrc
        }")`;
    },
  },
  created() {
    this.initSeasonPage();
    this.checkScreenWidth();
  },
  // mounted() {
  //   this.getEpisodesCast();
  // },
  methods: {
    checkScreenWidth() {
      if (window.innerWidth <= 768) {
        this.isMobile = true;
      }
    },
    async initSeasonPage() {
      this.$store.commit('clearError');
      this.$store.commit('setLoading', true);

      await this.getTvDetails();
      await this.getSeasonDetails();
      await this.getCredits();
      await this.getImages();
      await this.getVideos();

      // await this.getEpisodesCast();
      this.parseTrailer();
      this.parseVotes();

      this.$store.commit('setLoading', false);
    },
    async getTvDetails() {
      try {
        const res = await axios.get(
          `${base.urlPrefix}/tv/${this.tvId}?api_key=${base.key}&${base.language}`,
        );

        const { data } = res;

        this.backdropPath = data.backdrop_path;
        this.tvName = data.name;
        this.genres = data.genres;
      } catch (error) {
        this.$store.commit(
          'setError',
          `Uups! Do is wos schief glaffa: ${error.message}`,
        );
      }
    },
    async getSeasonDetails() {
      try {
        const res = await axios.get(
          `${base.urlPrefix}/tv/${this.tvId}/season/${this.seasonNumber}?api_key=${base.key}&${
            base.language
          }`,
        );

        const { data } = res;

        this.posterPath = data.poster_path;
        this.seasonName = data.name;
        this.airDate = data.air_date;
        this.overview = data.overview;
        this.episodes = data.episodes;
      } catch (error) {
        this.$store.commit(
          'setError',
          `Uups! Do is wos schief glaffa: ${error.message}`,
        );
      }
    },
    async getCredits() {
      try {
        const res = await axios.get(
          `${base.urlPrefix}/tv/${this.tvId}/season/${this.seasonNumber}/credits?api_key=${base.key}`,
        );

        const { data } = res;

        if (data.cast.length > 0) {
          this.cast = _.orderBy(data.cast, 'order', 'asc');
        }
        if (data.crew.length > 0) {
          this.crew = data.crew;
        }
      } catch (error) {
        this.$store.commit(
          'setError',
          `Uups. Do is wos schief glaffa: ${error.message}`,
        );
      }
    },
    async getImages() {
      try {
        const res = await axios.get(
          `${base.urlPrefix}/tv/${this.tvId}/season/${this.seasonNumber}/images?api_key=${base.key}`,
        );

        const { data } = res;
        this.posters = data.posters;
      } catch (error) {
        this.$store.commit(
          'setError',
          `Uups. Do is wos schief glaffa: ${error.message}`,
        );
      }
    },
    async getVideos() {
      try {
        let res = await axios.get(
          `${base.urlPrefix}/tv/${this.tvId}/season/${this.seasonNumber}/videos?api_key=${base.key}&${base.language}`,
        );
        if (res.data.results.length > 0) {
          const { data } = res;
          this.videos = data.results;
        } else {
          res = await axios.get(
            `${base.urlPrefix}/tv/${this.tvId}/season/${this.seasonNumber}/videos?api_key=${base.key}`,
          );
          const { data } = res;
          this.videos = data.results;
        }
      } catch (error) {
        this.$store.commit(
          'setError',
          `Uups. Do is wos schief glaffa: ${error.message}`,
        );
      }
    },
    getCast(type, episodeNumber) {
      let actors;
      switch (type) {
        case 'cast':
          this.episodesCast.forEach((element) => {
            if (element.episode_number === episodeNumber) {
              actors = element.cast;
            }
          });
          break;
        case 'guest':
          this.episodesGuests.forEach((element) => {
            if (element.episode_number === episodeNumber) {
              actors = element.cast;
            }
          });
          break;
        default:
          actors = [];
      }
      // console.log('actors', actors);

      return actors;
    },
    getEpisodesCast() {
      this.episodes.forEach((episode) => {
        this.fetchEpisodeCast(episode.episode_number);
      });
    },
    async fetchEpisodeCast(episodeNumber) {
      const res = await axios
        .get(`${base.urlPrefix}/tv/${this.tvId}/season/${this.seasonNumber}/episode/${episodeNumber}/credits?api_key=${base.key}`);

      const { data } = res;

      if (data.cast.length > 0) {
        const orderedCast = _.orderBy(data.cast, 'order', 'asc');
        this.episodesCast.push({ episode_number: episodeNumber, cast: orderedCast });
      }

      if (data.guest_stars.length > 0) {
        const orderedGuests = _.orderBy(data.cast, 'order', 'asc');
        this.episodesGuests.push({ episode_number: episodeNumber, cast: orderedGuests });
      }
    },
    parseTrailer() {
      // console.log('videos:', this.videos);

      if (this.videos.length !== 0) {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < this.videos.length; i++) {
          const video = this.videos[i];
          if (video.type === 'Trailer') {
            this.trailer = video;
            break;
          }
        }
      } else {
        this.trailer = '';
      }

      // console.log('parcedTrailer:', this.trailer);
    },
    parseVotes() {
      let votes = 0;
      let voteCounts = 0;
      if (this.episodes.length > 0) {
        this.episodes.forEach((episode) => {
          if (episode.vote_average > 0) {
            voteCounts += 1;
            votes += episode.vote_average;
          }
        });
      }
      if (votes > 0 && this.episodes.length > 0) {
        this.voteAverage = votes / voteCounts;
      } else {
        this.voteAverage = 0;
      }
    },
    getVoteColor(voteAverage) {
      let color;

      if (voteAverage === 0 || voteAverage === '') color = '#bdbdbd';
      if (voteAverage >= 1 && voteAverage < 5) color = 'error';
      if (voteAverage >= 5 && voteAverage < 7) color = 'warning';
      if (voteAverage >= 7) color = 'success';

      return color;
    },
    parseLocalDate(dateString) {
      let localDate = '';
      if (dateString) {
        localDate = new Date(dateString).toLocaleDateString();
      }
      return localDate;
    },
    onGenreClick() {
      // TODO: add TVShow Genre
      this.$store.commit('clearUserMessage');
      this.$store.commit('setUserMessage', 'Kimmd no!...');
    },
    showMore() {
      // TODO: add Details Modal!
      this.$store.commit('clearUserMessage');
      this.$store.commit('setUserMessage', 'Kimmd no!...');
    },
    getReadMoreText(text) {
      let shortText;
      const length = this.$vuetify.breakpoint.lgAndUp ? 180 : 400;
      const suffix = ' ...';
      if (text.length <= length) {
        shortText = text;
      } else {
        shortText = text.substring(0, length) + suffix;
      }
      return shortText;
    },
  },
};
</script>

<style lang="scss" scoped>
.v-lazy-image {
  display: block;
  width: 100%;
  height: 80%;
  filter: blur(10px);
  transition: filter 0.7s;
}

.v-lazy-image-loaded {
  filter: blur(0);
}

.season {
  position: relative;
  height: 100%;

  &__top {
    position: relative;
    background-size: cover;
    background-position: center top;
  }

  &__top-wrapper {
    position: relative;
    display: flex;
    @media screen and (min-width: 1265px) {
      max-height: 50vh;
    }
    @media screen and (max-width: 1264px) {
      max-height: 35vh;
    }
  }

  &__picture {
    position: relative;
    min-width: 25%;
    max-height: 450px;
    animation-delay: 0.5s;
    overflow: hidden;
  }

  &__img {
    display: block;
    width: 100%;
  }

  &__top-content {
    width: 100%;
    // min-height: 450px;
    animation-delay: 0.5s;
    overflow: hidden;
  }

  &__name {
    display: flex;
    font-size: 20px;
    margin-left: 30px;
    font-weight: 700;


    @media (max-width: 768px) {
      font-weight: 500;
      font-size: 18px;
      margin-left: 0;
    }

    @media (max-width: 600px) {
      line-height: 18px;
      margin-bottom: 10px;
    }
  }

  &__genres {
    padding: 5px 15px 0px;
    display: flex;

    @media (max-width: 768px) {
      padding: 5px 0 0;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    padding-left: 30px;
    margin-top: 5px;
    border-top: 1px solid #546e7a;
    background-color: rgba(0, 0, 0, 0.6);

    @media (max-width: 768px) {
      margin-top: 10px;
      padding: 0 20px;
    }

    @media (max-width: 425px) {
      display: flex;
      justify-content: space-between;
    }
  }

  &__items {
    padding: 10px 30px;
    margin-top: 10px;
    list-style: none;
    font-size: 1em;
    font-weight: 380;

    border-top: 1px solid #546e7a;
    background-color: rgba(0, 0, 0, 0.6);
    text-align: justify;

    @media (max-width: 960px) {
      // padding: 10px 0 10px 30px;
      font-size: 14px;
    }

    @media (max-width: 768px) {
      padding: 10px 20px;
      font-size: 14px;
    }

    @media (max-width: 600px) {
      margin-top: 15px;
      font-size: 13px;
    }

    span {
      font-weight: 400;
    }
  }
  &__items-title {
    font-weight: 400;
    font-size: 20px;
    margin-bottom: 10px;
    border-bottom: 1px solid #546e7a;
  }

  &__episode-card {
    transition: transform 0.3s ease;
    &:hover {
      @media only screen and (min-width: 961px){
        cursor: default;
        transform: translateY(-5px);
        z-index: 2;
      }
    }
  }

  &__episode-title {
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    margin-bottom: 8px;
  }

  &__episode-date {
    display: inline-block;
    white-space: nowrap;
    font-size: 0.9em;
    font-weight: normal;
    line-height: 1.4em;
  }

  &__episode-content {
    // height: 8vh;
    overflow: hidden;
    text-align: justify;
  }

  &__episode-img {
    min-width: 25%;
    max-width: 25%;
    max-height: 150px;
    margin-right: 20px;
  }

  &__episode-overview {
    display: block;
    width: 100%;
    font-size: 1em;
    font-weight: 380;
    text-align: justify;
    margin: 5px 10px 10px 0;
  }

  &__episodes-title,
  &__similar-title {
    width: 100%;
    font-weight: 300;
    margin-bottom: 15px;
    font-size: 20px;
    border-bottom: 1px solid #546e7a;

    @media (max-width: 768px) {
      font-size: 18px;
    }

    @media (max-width: 600px) {
      margin-bottom: 10px;
    }
  }

  &__tab-overview {
    font-weight: 300;
    text-align: justify;
  }

  &__no-actors {
    font-size: 14px;
    font-weight: 300;
    text-align: justify;

    @media (max-width: 600px) {
      font-size: 13px;
    }
  }

}

</style>
